import { useMemo, useState } from "react";
import clsx from "clsx";
import { useTranslations } from "use-intl";
import * as Sentry from "@sentry/react";
import {
  createFileRoute,
  type ErrorComponentProps,
  Navigate,
  Outlet,
  useMatchRoute,
  useRouter,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button, Dialog, DialogContent } from "@mui/material";
import Header from "@/app/ui/flight-purchase/Header";
import { WhatsAppButton } from "@/app/ui/WhatsAppButton";
import { useFlightSearchDataContext } from "@/app/lib/context/FlightSearchDataContext";
import { flightAvailabilityQueryOptions } from "@/app/lib/hooks/useFlightAvailability";
import CancelFlightDrawer from "@/app/ui/flight-purchase/CancelFlightPurchase";
import { TravellersSelectionProvider } from "@/app/lib/context/TravellersSelectionContext";
import { ErrorPanel } from "@/app/ui/ErrorPanel";
import { IconSadFace } from "@/app/icons";
import { usePurchaseStateContext } from "@/app/lib/context/PurchaseStateContext";
import { BookingError } from "@/app/ui/errors/BookingError";
import { useRouteConfig } from "@/app/lib/hooks/useRouteConfig";
import { isOfferIsNotAvailableAnymoreError } from "@/app/lib/assertions";
import { OfferNotAvailableError } from "@/app/ui/OfferNotAvailableError";
import { type OfferSelectedEventData } from "@/app/lib/types/types";
import { useFlightSelectionPayloadContext } from "@/app/lib/context/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { travellersQueryOptions } from "@/app/lib/hooks/useTravellers";

export const Route = createFileRoute("/_authenticated/_flights/_flight-offer")({
  component: RouteComponent,
  errorComponent: (props: ErrorComponentProps) => {
    Sentry.captureException(props.error);

    if (isOfferIsNotAvailableAnymoreError(props.error)) {
      return <OfferNotAvailableError variant="flight" />;
    }

    return <BookingError {...props} type="flights" />;
  },
  onEnter: () => {
    const event = new CustomEvent<OfferSelectedEventData>("offerselected", {
      detail: {
        offerType: "air",
      },
    });
    window.dispatchEvent(event);
  },
});

function RouteComponent() {
  const { history } = useRouter();
  const routeConfig = useRouteConfig();
  const t = useTranslations();

  const { selectedFlightPayload } = useFlightSelectionPayloadContext();

  const { searchData } = useFlightSearchDataContext();
  const { purchaseState } = usePurchaseStateContext();
  useQuery(travellersQueryOptions()); // for preloading
  const { error } = useQuery(
    flightAvailabilityQueryOptions(selectedFlightPayload),
  );

  const [cancelFlightOpen, setCancelFlightOpen] = useState(false);

  const matches = useMatchRoute();

  const isTicketPurchasedRoute = matches({
    to: "/flights/offer/$id/step/final",
    from: "/",
  });

  const initialTravellersSlots = useMemo(() => {
    if (!searchData) return;

    return {
      ADT: Array.from({ length: searchData.adult }, () => null),
      CHD: Array.from({ length: searchData.child }, () => null),
      INF: Array.from({ length: searchData.infantWithoutSeat }, () => null),
    };
  }, [searchData]);

  if (!isTicketPurchasedRoute && purchaseState === "completed") {
    return <Navigate to="/" />;
  }

  return (
    <TravellersSelectionProvider
      initialTravellersSlots={initialTravellersSlots}
      travelDate={new Date(searchData!.dates.departure)}
    >
      <main
        className={clsx("no-footer main", {
          "with-drawer": !!routeConfig.drawer,
        })}
      >
        <div className="mt-2 mb-4">
          {!isTicketPurchasedRoute ? (
            <Header
              searchData={searchData!}
              onCancel={() => setCancelFlightOpen(true)}
              onBack={history.back}
            />
          ) : null}

          <div>
            <Outlet />
          </div>

          <CancelFlightDrawer
            open={cancelFlightOpen}
            closeDrawer={() => setCancelFlightOpen(false)}
          />

          <Dialog
            open={isOfferIsNotAvailableAnymoreError(error)}
            fullWidth
            maxWidth="xs"
          >
            <DialogContent>
              <ErrorPanel
                icon={IconSadFace}
                primaryText={t("flights.notAvailable.title")}
                secondaryText={t("flights.notAvailable.description")}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    history.back();
                  }}
                  sx={{ borderRadius: "var(--radius-2xl)" }}
                >
                  {t("flights.notAvailable.button")}
                </Button>
              </ErrorPanel>
            </DialogContent>
          </Dialog>

          {!isTicketPurchasedRoute && <WhatsAppButton bottomPosition="20vh" />}
        </div>
      </main>
    </TravellersSelectionProvider>
  );
}
